<template>
  <div class="rapipago-page">
    <div class="rapipago-page__wrap">
      <div v-if="templateData.logo" class="rapipago-page__logo">
        <img :src="templateData.logo" alt="" />
      </div>
      <div class="rapipago-page__id">
        {{ $t('common__invoice') }} {{ templateData.reference_id }}
      </div>
      <div v-if="templateData.description" class="rapipago-page__descriptions">
        {{ templateData.description }}
      </div>
      <div class="rapipago-page__info">
        <div class="rapipago-page__info-label">
          {{ $t('common__amount_to_pay') }}
        </div>
        <div class="rapipago-page__info-value">
          <span v-if="templateData.symbol" class="rapipago-page__info-value__sybmol">
            {{ templateData.symbol }}
          </span>
          <div class="rapipago-page__info-value__amount">
            <div class="rapipago-page__info-value__amount-first">
              {{ templateData.amount.firstPart }}
            </div>
            <div v-if="templateData.amount.lastPart" class="rapipago-page__info-value__amount-last">
              {{ templateData.amount.lastPart }}
            </div>
          </div>
          <span v-if="!templateData.symbol" class="rapipago-page__info-value__currency">
            {{ templateData.currency }}
          </span>
          <ButtonCopy :value="templateData.amount.full.toString()" is-gray />
        </div>
      </div>
      <template v-if="templateData.barcode">
        <div class="rapipago-page__barcode">
          <svg ref="barcodeSvg"></svg>

          <div class="rapipago-page__barcode-link">
            <ButtonCopy
              :value="templateData.barcode"
              has-border
              class="rapipago-page__barcode-btn"
              is-gray>
              <div>{{ $t('invoice_flow__multi_copy_code') }}</div>
            </ButtonCopy>
          </div>
        </div>
      </template>

      <div v-for="(step, index) in steps" :key="index" class="rapipago-page__step">
        <div class="rapipago-page__step-order"></div>
        <div class="rapipago-page__step-label">
          {{ $t(step) }}
        </div>
      </div>

      <button class="rapipago-page__button" @click="redirectReturnUrl">
        {{ $t('invoice_flow__card_transfer_button') }}
      </button>
    </div>
    <div :class="{ 'no-btn': !templateData.has_return_url }" class="rapipago-page__footer">
      <div
        v-if="templateData.has_return_url"
        class="rapipago-page__footer-back"
        @click="redirectReturnUrl">
        <img src="./assets/arrow.svg" alt="" />

        <span>
          {{ $t('invoice_flow__multi_return_button') }}
        </span>
      </div>
      <div class="rapipago-page__footer-locale">
        <img src="./assets/locale.svg" alt="" />
        <div
          v-for="(locale, index) in locales"
          :key="index"
          :class="{ active: locale === currentLocale }"
          class="rapipago-page__footer-locale__item"
          @click="selectLocale(locale)">
          {{ locale.toUpperCase() }}
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import { setPageTitle } from '@/helpers/setPageTitle';
import JsBarcode from 'jsbarcode';
import ButtonCopy from './components/ButtonCopy.vue';

export default {
  components: { ButtonCopy },
  created() {
    this.$i18n.locale = 'es';
    setPageTitle('Rapipago');
  },

  data() {
    return {
      loading: false,
    };
  },

  mounted() {
    if (this.templateData.barcode) {
      this.generateBarcode(this.templateData.barcode);
    }
  },
  computed: {
    ...mapState(['paymentInvoiceData', 'flowData']),

    locales() {
      return ['es', 'en'];
    },

    currentLocale() {
      return this.$i18n.locale;
    },

    steps() {
      return ['invoice_flow__multi_rapipago_step_1', 'invoice_flow__multi_rapipago_step_2'];
    },

    paymentInvoice() {
      return this.paymentInvoiceData ? this.paymentInvoiceData.data || this.paymentInvoiceData : {};
    },

    templateData() {
      const { reference_id, description, has_return_url, id, expires } = this.paymentInvoice;
      const { amount: mainAmount, currency: mainCurrency } = this.flowData;

      const {
        default_locale = null,
        method,
        requisites,
        amount: payloadAmount,
        currency: payloadCurrency,
      } = this.flowData.payload;

      const parseRequisites = () => {
        if (!requisites) {
          return {};
        }

        if (typeof requisites === 'string') {
          try {
            return JSON.parse(requisites);
          } catch (e) {
            return {};
          }
        }

        return requisites;
      };

      const { barcode } = parseRequisites();

      const amount = [undefined, null].includes(payloadAmount) ? mainAmount : payloadAmount;
      const currency = [undefined, null].includes(payloadCurrency) ? mainCurrency : payloadCurrency;

      return {
        id,
        reference_id,
        description,
        logo: (method || {}).logo || null,
        amount: this.parseAmount(amount),
        symbol: this.currencySymbol(currency),
        currency,
        default_locale,
        has_return_url,
        barcode,
      };
    },
  },
  methods: {
    selectLocale(locale) {
      this.$bus.$emit('changeLocale', locale);
    },

    generateBarcode(value) {
      if (this.$refs.barcodeSvg) {
        JsBarcode(this.$refs.barcodeSvg, value, {
          format: 'CODE128',
          lineColor: '#000',
          width: 2,
          height: 100,
          displayValue: true,
        });
      }
    },

    parseAmount(amount) {
      const [firstPart, lastPart] = amount.toString().split('.');

      return {
        full: amount,
        firstPart,
        lastPart: parseInt(lastPart) > 0 ? `.${lastPart}` : null,
      };
    },

    currencySymbol(currency) {
      switch (currency) {
        case 'UAH':
          return '₴';
        case 'USD':
          return '$';
        case 'EUR':
          return '€';
        case 'RUB':
          return '₽';
        default:
          return null;
      }
    },

    redirectReturnUrl() {
      if (!this.templateData.has_return_url) {
        return;
      }

      location.replace(`/return?id=${this.paymentInvoice.id}&force=true`);
    },
  },
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
$--blue-color: #334dff;
$--white-color: #ffffff;

$--label-color-1: #7f8fa4;
$--label-color-2: #334150;
$--card-label-color: #b3bdff;
$--card-value-color: $--white-color;

$--step-bg-color: #f1f3ff;
$--step-label-color-1: $--blue-color;

$--button-bg-color: #0020ff;
$--button-hover-bg-color: $--blue-color;
$--button-active-bg-color: #001acc;
$--button-disabled-bg-color: #d6daf5;

@media (max-width: 480px) {
  canvas {
    width: 120px !important;
    height: 120px !important;
  }
}

.rapipago-page {
  display: flex;
  align-items: center;
  flex-direction: column;
  font-family: 'Roboto', sans-serif;

  &__logo {
    margin-bottom: 16px;
    img {
      height: 100px;
    }

    @media (max-width: 480px) {
      margin-bottom: 12px;
    }
  }

  &__alt {
    padding: 4px 0;
    margin-bottom: 16px;
    position: relative;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: #cdd5db;

    &::before,
    &::after {
      content: '';
      position: absolute;
      height: 2px;
      background: #eaecee;
      width: calc(43% - 16px);
      top: calc(50% - 2px);
    }

    &::before {
      left: 0;
    }

    &::after {
      right: 0;
    }
  }

  &__qr-link {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: center;
    margin-bottom: 24px;
    position: relative;

    .el-input {
      width: 100%;
      margin: 0 60px;

      @media (max-width: 480px) {
        margin: 30px;
      }

      &__inner {
        width: 100%;
        padding: 10px 16px;
        border: 1px solid #0020ff;
        border-radius: 6px;
        color: #334150;
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.5px;
        outline: unset;
        transition: border-color 0.3s;
      }
    }

    &-btn {
      position: absolute;
      right: 28px;

      @media (max-width: 480px) {
        right: 0px;
      }
    }
  }

  &__card {
    box-shadow: 0px 8px 24px -18px rgba(91, 105, 204, 0.32);
    border-radius: 12px;
    max-width: 340px;
    width: 100%;
    margin: 16px auto 32px;
    position: relative;
    overflow: hidden;

    &-bg {
      display: block;
      width: 100%;
      position: absolute;
      z-index: 0;
      top: 0;
      left: 0;
      height: 100%;
    }

    &-wrap {
      position: inherit;
      width: 100%;
      min-height: 192px;
      padding: 24px 24px 24px 20px;
      z-index: 1;
    }

    &-header,
    &-main,
    &-footer {
      display: flex;
      justify-content: space-between;
    }

    &-main {
      margin: 32px 0;

      &.max {
        margin: 48px 0 32px;

        @media (max-width: 380px) {
          margin: 32px 0 26px;
        }
      }

      @media (max-width: 380px) {
        margin: 26px 0;
      }

      &__button {
        display: flex;
        align-items: center;
      }

      .button-copy {
        @media (max-width: 380px) {
          width: 18px;
          height: 18px;

          svg {
            width: 8px;
          }
        }
      }

      &__number {
        display: flex;
        gap: 12px;
        color: $--card-value-color;
        letter-spacing: 2px;
        font-weight: 700;
        font-size: 20px;
        line-height: 24px;

        @media (max-width: 460px) {
          font-size: 16px;
          gap: 8px;
        }

        @media (max-width: 380px) {
          font-size: 14px;
          gap: 4px;
        }
      }
    }

    &-footer {
      &__col {
        &-label {
          font-weight: 500;
          font-size: 12px;
          line-height: 16px;
          color: $--card-label-color;
          margin-bottom: 4px;
          text-align: left;
          @media (max-width: 380px) {
            font-size: 10px;
          }
        }
        &-value {
          font-weight: 500;
          font-size: 14px;
          line-height: 16px;
          color: $--card-value-color;
          text-align: right;

          @media (max-width: 380px) {
            font-size: 12px;
          }
          &.holder {
            text-align: left;
          }
        }
      }
    }
  }

  &__deep-link {
    padding: 8px 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border: 1px solid $--blue-color;
    border-radius: 6px;
    width: fit-content;
    margin: 0 auto 24px;

    @media (max-width: 480px) {
      margin: 0 auto 16px;
    }

    &__img {
      height: 16px;
    }

    &__text {
      letter-spacing: 0.5px;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
    }
  }

  &__qr {
    margin-bottom: 24px;
  }

  &__barcode {
    & > svg {
      max-width: 100%;
      height: 100px;
      object-fit: contain;
      margin-bottom: 20px;

      @media (max-width: 480px) {
        height: 80px;
        margin-bottom: 10px;
      }
    }

    &-link {
      display: flex;
      align-items: center;
      gap: 8px;
      justify-content: center;
      margin-bottom: 24px;
      position: relative;

      .el-input {
        width: 100%;
        margin: 0 60px;
        input {
          text-overflow: ellipsis;
        }

        @media (max-width: 480px) {
          margin: 0 30px;
        }

        &__inner {
          width: 100%;
          padding: 10px 16px;
          border: 1px solid #0020ff;
          border-radius: 6px;
          color: #334150;
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
          letter-spacing: 0.5px;
          outline: unset;
          transition: border-color 0.3s;
        }
      }
    }
    .button-copy {
      width: fit-content;
      padding: 15px 20px;
      gap: 5px;
    }
  }

  &__wrap {
    max-width: 540px;
    width: 100%;
    padding: 32px;
    box-shadow: 0px 50px 60px -30px rgba(50, 74, 101, 0.15);
    border-radius: 10px;
    background: #ffffff;
  }

  &__footer {
    max-width: 540px;
    width: 100%;
    margin: 16px 0;
    display: flex;
    justify-content: space-between;

    &.no-btn {
      justify-content: flex-end;
    }

    &-back {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.5px;
      color: $--button-bg-color;
      display: flex;
      gap: 9px;
      cursor: pointer;
      align-items: center;

      img {
        height: 6.63px;
      }
    }

    &-locale {
      display: flex;
      align-items: center;
      &__item {
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        color: #7f8fa4;
        transition: color 0.3s;
        border-right: 1px solid #cdd5db;
        cursor: pointer;
        padding: 0 8px;

        &.active,
        &:hover {
          color: $--button-bg-color;
        }

        &:first-child {
          padding: 0 8px 0 9.5px;
        }

        &:last-child {
          border-right: unset;
          padding: 0 0 0 8px;
        }
      }
    }
  }

  &__title {
    letter-spacing: 0.5px;
    color: $--label-color-1;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 4px;
  }

  &__id {
    letter-spacing: 1px;
    font-weight: 500;
    font-size: 20px;
    line-height: 24px;
    color: $--label-color-2;
    margin-bottom: 12px;
  }

  &__descriptions {
    text-align: center;
    letter-spacing: 0.5px;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: $--label-color-1;
    margin-bottom: 24px;

    @media (max-width: 480px) {
      margin-bottom: 16px;
    }
  }

  &__info {
    margin-bottom: 20px;

    @media (max-width: 480px) {
      margin-bottom: 16px;
    }

    &-label {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.5px;
      color: $--label-color-1;
      margin-bottom: 4px;
    }

    &-value {
      display: flex;
      justify-content: center;
      align-items: center;
      letter-spacing: 1px;
      font-weight: 500;
      font-size: 24px;
      line-height: 32px;
      color: $--label-color-2;
      &__amount {
        margin: 0 8px;
        display: flex;
        flex-wrap: wrap;
        justify-content: center;

        &-last {
          line-height: 27px;
          align-self: flex-end;
          font-size: 16px;
        }
      }
    }
  }

  &__step {
    display: grid;
    grid-template-columns: 20px auto;
    grid-gap: 8px;
    margin-top: 12px;

    &-order {
      font-weight: 500;
      font-size: 10px;
      line-height: 16px;
      letter-spacing: 0.5px;
      font-feature-settings: 'liga' off;
      background: #f1f3ff;
      color: $--blue-color;
      border-radius: 50%;
      width: 20px !important;
      height: 20px;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &-label {
      text-align: left;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: $--label-color-2;
    }
  }

  &__button {
    background: $--button-bg-color;
    border-radius: 6px;
    padding: 12px;
    width: 100%;
    max-width: 340px;
    margin: 32px auto 0;
    letter-spacing: 0.5px;
    font-weight: 700;
    font-size: 14px;
    line-height: 16px;
    color: $--white-color;
    cursor: pointer;
    transition: background 0.3s;
    position: relative;
    overflow: hidden;
    border: unset;

    @media (max-width: 480px) {
      margin: 24px auto 0;
    }

    &-loader {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      background: #0000006e;
    }

    &:hover {
      background: $--button-hover-bg-color;
    }

    &:active {
      background: $--button-active-bg-color;
    }

    &:disabled {
      background: $--button-disabled-bg-color;
      cursor: not-allowed;
    }
  }

  &__message {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.5px;
    color: $--label-color-1;
    margin: 16px auto;
    width: 100%;
    max-width: 340px;
  }
}
</style>
